import { 
  Box, Flex, Text, Image, VStack, Badge, 
  useColorModeValue, Link, Heading 
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import newLaunchImg from "../../assets/images/NewLaunchImage.png";

// Unique "Calendar Scroll" Digit Component
const ScrollingDigit = ({ value, label }) => {
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("black", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.300");

  return (
    <VStack spacing={2}>
      <Box
        position="relative"
        bg={bgColor}
        w={{ base: "60px", md: "80px" }}
        h={{ base: "70px", md: "90px" }}
        borderRadius="xl"
        border="1px solid"
        borderColor={borderColor}
        boxShadow="xl"
        overflow="hidden"
      >
        <AnimatePresence mode="popLayout">
          <motion.div
            key={value}
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -40, opacity: 0 }}
            transition={{ duration: 0.4, ease: "backOut" }}
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text
              fontSize={{ base: "3xl", md: "5xl" }}
              fontWeight="900"
              color={textColor}
              fontFamily="Bricolage Grotesque"
            >
              {value.toString().padStart(2, "0")}
            </Text>
          </motion.div>
        </AnimatePresence>
        {/* Horizontal Card Fold Line */}
        <Box 
          position="absolute" top="50%" w="full" h="1px" 
          bg={borderColor} opacity={0.5} 
        />
      </Box>
      <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
        {label}
      </Text>
    </VStack>
  );
};

const SomethingBigIsComingSection = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const bgGradient = useColorModeValue(
    "radial(blue.50, transparent)", 
    "radial(rgba(245, 158, 11, 0.05), transparent)"
  );

  useEffect(() => {
    const targetDate = new Date("2026-10-30");
    const calculateTime = () => {
      const now = new Date();
      const diff = targetDate - now;
      if (diff <= 0) return;
      
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / 1000 / 60) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };

    const interval = setInterval(calculateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box position="relative" overflow="hidden" py={{ base: 12, md: 24 }}>
      {/* Background Decorative Element */}
      <Box 
        position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)"
        w="100%" h="100%" bgGradient={bgGradient} zIndex={-1}
      />

      <Flex
        maxW="7xl" mx="auto" px={6}
        alignItems="center"
        justifyContent="space-between"
        direction={{ base: "column", md: "row" }}
        gap={12}
      >
        {/* Left: Dynamic Image with Floating Animation */}
        <motion.div
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          style={{ width: "100%", maxWidth: "500px" }}
        >
          <Image
            src={newLaunchImg}
            alt="New Launch"
            w="full"
            filter="drop-shadow(0 20px 30px rgba(0,0,0,0.1))"
          />
        </motion.div>

        {/* Right: Content */}
        <VStack flex="1" align={{ base: "center", md: "flex-start" }} spacing={8}>
          <Badge
            colorScheme="yellow"
            px={4} py={1} rounded="full"
            variant="subtle"
            fontSize="sm" letterSpacing="wider"
          >
            🌟 ENROLLMENTS OPEN NOW
          </Badge>

          <Box textAlign={{ base: "center", md: "left" }}>
            <Heading
              fontSize={{ base: "4xl", md: "6xl", lg: "7xl" }}
              fontWeight="900"
              lineHeight="1"
              fontFamily="Bricolage Grotesque"
              mb={4}
            >
              Become a <br />
              <Text as="span" color="#F59E0B">Full Stack Java Developer</Text> <br />
              in Just 3 Months
            </Heading>
          </Box>

          <Text
            fontSize={{ base: "md", md: "lg" }}
            color={useColorModeValue("gray.600", "gray.400")}
            textAlign={{ base: "center", md: "left" }}
            maxW="600px"
            lineHeight="tall"
          >
            Our <b>Complete Full Stack Java Developer Bootcamp</b> kicks off on{" "}
            <b>September 01, 2026</b>. Learn HTML, CSS, JavaScript, React, Java, Springboot, PostgreSQL
            and more — build real projects, ship a portfolio, and graduate
            job-ready. Seats are limited, so lock yours in early.
          </Text>

          {/* Unique Animated Counter */}
          <Flex gap={{ base: 3, md: 6 }} py={4}>
            <ScrollingDigit value={timeLeft.days} label="Days" />
            <ScrollingDigit value={timeLeft.hours} label="Hours" />
            <ScrollingDigit value={timeLeft.minutes} label="Mins" />
            <ScrollingDigit value={timeLeft.seconds} label="Secs" />
          </Flex>

          <Link
            href="/bootcamps"
            fontSize="lg"
            fontWeight="bold"
            color="#F59E0B"
            _hover={{ textDecoration: "none", transform: "translateX(5px)" }}
            transition="all 0.2s"
          >
            Reserve Your Seat Now →
          </Link>
        </VStack>
      </Flex>
    </Box>
  );
};

export default SomethingBigIsComingSection;